import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAppSelector } from '../store';
import { useActiveEvent } from '../contexts/EventContext';
import { API_BASE_URL, SOCKET_URL } from '../lib/utils';
import { getEventMapCenter } from '../lib/eventLocation';
import MapDrawingTools from '../components/map/MapDrawingTools';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Loader2 } from 'lucide-react';
import type { Incident } from '../types';

export default function MapPage() {
  const { token } = useAppSelector((state) => state.auth);
  const { activeEventId, activeEvent, loading: eventLoading } = useActiveEvent();
  const eventMapCenter = useMemo(() => getEventMapCenter(activeEvent), [activeEvent]);
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<Incident[]>([]);

  const fetchIncidents = useCallback(async () => {
    if (!token) return;
    try {
      const url = activeEventId
        ? `${API_BASE_URL}/incidents?eventId=${encodeURIComponent(activeEventId)}`
        : `${API_BASE_URL}/incidents`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setIncidents(data.incidents || data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    }
  }, [token, activeEventId]);

  useEffect(() => {
    fetchIncidents();
    const interval = setInterval(fetchIncidents, 30000);
    return () => clearInterval(interval);
  }, [fetchIncidents]);

  // Real-time incident updates (same as split ops view).
  useEffect(() => {
    if (!token) return;
    const socket = io(SOCKET_URL, { transports: ['websocket'], auth: { token } });
    socket.on('incident:updated', (updated: Incident) => {
      setIncidents((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      fetchIncidents();
    });
    socket.on('incident:created', (created: Incident) => {
      setIncidents((prev) => [created, ...prev]);
      fetchIncidents();
    });
    return () => {
      socket.disconnect();
    };
  }, [token, fetchIncidents]);

  const handleIncidentClick = (incident: Incident) => {
    if (window.opener) {
      window.opener.postMessage({ type: 'NAVIGATE_TO_INCIDENT', incidentId: incident.id }, '*');
    } else {
      const query = activeEventId ? `?eventId=${encodeURIComponent(activeEventId)}` : '';
      navigate(`/workspace/incidents/${incident.id}${query}`);
    }
  };

  const eventLabel = activeEvent?.name || activeEventId || 'All events';

  return (
    <div className="h-screen w-screen bg-background flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
            <span className="text-primary font-bold text-sm">C2</span>
          </div>
          <div>
            <h1 className="text-sm font-bold text-foreground">Tactical Map</h1>
            <p className="text-xs text-muted-foreground truncate max-w-[280px]">
              {eventLabel} — shared overlays, drawings &amp; tracking
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span>Live</span>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 min-h-0 relative">
        {activeEventId && eventLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading event map…
            </div>
          </div>
        ) : (
          <MapDrawingTools
            className="h-full"
            incidents={incidents}
            onIncidentClick={handleIncidentClick}
            isPanel={false}
            autoFitContent
            center={eventMapCenter}
            zoom={eventMapCenter ? 15 : 13}
          />
        )}
      </div>
    </div>
  );
}
